import React, {
  lazy, Suspense, useEffect, useState, useContext
} from 'react';
import {
  string,
  func,
  bool,
  shape,
  oneOfType,
  oneOf
} from 'prop-types';

import {
  useConfigService,
  useStore,
  ExperienceContext
} from '@thd-nucleus/experience-context';
import {
  arrayOf as arrayOfType,
  bool as boolType,
  client,
  extend,
  number as numberType,
  params,
  shape as shapeType,
  string as stringType,
  useDataModel,
} from '@thd-nucleus/data-sources';

import { PromotionProductsLoader } from './PromotionProductsLoader/PromotionProductsLoader';
import { isCompatiblePromotion } from '../utils/promo-model-utils';
import { getIsFeatureEnabled, isPromotionEnabled } from '../utils/promo-utils';
import { useMACheckAvailability } from '../hooks/useMACheckAvailability';
import { EXPERIENCE_TAGS, FEATURE_SWITCH_KEYS } from '../utils/constants';
import { CallToActionDataModel } from '../models/CallToActionDataModel';

const PromotionProductsLazy = lazy(() => import(
  /* webpackChunkName: "promotion-products-lazy" */
  './PromotionProductsLazy'
).then((mod) => {
  return {
    default: (props) => {
      const { PromotionProductsLazy: PromotionProductsComp } = mod;

      return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <PromotionProductsComp {...props} />
      );
    },
  };
}),
);

/**
 * @param {Object} props
 * Component props
 * @param {string} props.itemId
 * The itemId of the "Anchor Item". It is the promotion associated with that item that is displayed
 * @param {boolean | null} [props.bogoEnabled]
 * Enables BOGO promotions. The config service can enable or disable as well. Default: `true`
 * @param {boolean | null} [props.msbEnabled]
 * Enables MSB promotions. The config service can enable or disable as well. Default: `true`
 * @param {boolean | null} [props.bmsmEnabled]
 * Enables BMSM promotions. The config service can enable or disable as well. Default: `true`
 * @param {boolean | null} [props.backwardsBogoEnabled]
 * Enables Backwards BOGO promotions. The config service can enable or disable as well. Default: `false`
 * @param {boolean | null} [props.bogoDollarThresholdEnabled]
 * Enables BOGO Dollar Threshold promotions. The config service can enable or disable as well. Default: `false`
 * @param {(anchorItem: Object) => boolean} [props.isMultiSelect]
 * For BMSM promotions for major appliance products
 * @param {() => {} | null} [props.onFail]
 * For onFail fallback
 *
 * @returns {React.ReactElement | null}
 */
export const PromotionProducts = ({
  itemId,
  bogoEnabled: bogoEnabledProp,
  msbEnabled: msbEnabledProp,
  bmsmEnabled: bmsmEnabledProp,
  backwardsBogoEnabled: backwardsBogoEnabledProp,
  bogoDollarThresholdEnabled: bogoDollarThresholdEnabledProp,
  isMultiSelect,
  type,
  onFail
}) => {
  const [mounted, setMounted] = useState(false);
  const bogoEnabled = getIsFeatureEnabled({
    defaultValue: true,
    prop: bogoEnabledProp,
    config: useConfigService(FEATURE_SWITCH_KEYS.bogo),
  });
  const msbEnabled = getIsFeatureEnabled({
    defaultValue: true,
    prop: msbEnabledProp,
    config: useConfigService(FEATURE_SWITCH_KEYS.msb),
  });
  const bmsmEnabled = getIsFeatureEnabled({
    defaultValue: true,
    prop: bmsmEnabledProp,
    config: useConfigService(FEATURE_SWITCH_KEYS.bmsm),
  });
  const backwardsBogoEnabled = getIsFeatureEnabled({
    defaultValue: false,
    prop: backwardsBogoEnabledProp,
    config: useConfigService(FEATURE_SWITCH_KEYS.backwardsBogo),
  });
  const bogoDollarThresholdEnabled = getIsFeatureEnabled({
    defaultValue: false,
    prop: bogoDollarThresholdEnabledProp,
    config: useConfigService(FEATURE_SWITCH_KEYS.bogoDollarThreshold),
  });

  const isEnabled = bogoEnabled || msbEnabled || bmsmEnabled;

  const store = useStore();
  const { deliveryZip } = useContext(ExperienceContext);

  // QueryProvider that serves this is the one that is in the consumer (experience)
  // this is the request for the anchor item
  const { data, loading } = useDataModel('product', {
    variables: {
      itemId,
      storeId: store.storeId,
      zipCode: deliveryZip,
    },
    skip: !isEnabled,
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  // personalized deals are unknown on server, so for PHP Bogo card, we force the
  // loader until we have examined the data, which occurs after mounting
  const isForceLoader = type === 'card';

  const anchorItem = data?.product;
  const experienceTag = anchorItem?.pricing?.conditionalPromotions?.[0]?.experienceTag;
  const subExperienceTag = anchorItem?.pricing?.conditionalPromotions?.[0]?.subExperienceTag;

  const checkAvailabilityFulfillment = useMACheckAvailability({
    anchorItem,
    skip: experienceTag !== EXPERIENCE_TAGS.BOGO
  });

  const isDisplayablePromotion = (
    isCompatiblePromotion(anchorItem)
    && isPromotionEnabled({
      experienceTag,
      subExperienceTag,
      backwardsBogoEnabled,
      bogoDollarThresholdEnabled,
      bogoEnabled,
      bmsmEnabled,
      msbEnabled,
      type
    })
  );

  // server && client first render
  if (!mounted) {
    if (!isDisplayablePromotion && !isForceLoader) {
      return null;
    }

    return (
      <PromotionProductsLoader
        experienceTag={experienceTag}
        subExperienceTag={subExperienceTag}
        type={type}
      />
    );
  }

  if (!isDisplayablePromotion) {
    if (!loading && onFail) onFail(); // only report failure after we're done loading data
    if (!loading || !isForceLoader) return null;

    return (
      <PromotionProductsLoader
        experienceTag={experienceTag}
        subExperienceTag={subExperienceTag}
        type={type}
      />
    );
  }

  // we are mounted and we have a displayable promotion and request lazy bundle.
  // anchor fulfillment is checked in model provider.
  return (
    <Suspense fallback={(
      <PromotionProductsLoader
        experienceTag={experienceTag}
        subExperienceTag={subExperienceTag}
        type={type}
      />
    )}
    >
      {checkAvailabilityFulfillment.isLoading ? (
        <PromotionProductsLoader
          experienceTag={experienceTag}
          subExperienceTag={subExperienceTag}
          type={type}
        />
      ) : (
        <PromotionProductsLazy
          anchorItem={anchorItem}
          isMultiSelect={isMultiSelect && isMultiSelect(anchorItem)}
          checkAvailabilityFulfillment={checkAvailabilityFulfillment}
          type={type}
          onFail={onFail}
        />
      )}
    </Suspense>
  );
};

PromotionProducts.displayName = 'PromotionProducts';

PromotionProducts.propTypes = {
  /** The itemId of the "Anchor Item". It is the promotion associated with that item that is displayed */
  itemId: string.isRequired,
  /** Enables BOGO promotions. The config service can enable or disable as well. Default: `true` */
  bogoEnabled: oneOfType([bool, shape({})]),
  /** Enables MSB promotions. The config service can enable or disable as well. Default: `true` */
  msbEnabled: oneOfType([bool, shape({})]),
  /** Enables BMSM promotions. The config service can enable or disable as well. Default: `true` */
  bmsmEnabled: oneOfType([bool, shape({})]),
  /** Enables Backwards BOGO promotions. The config service can enable or disable as well. Default: `false` */
  backwardsBogoEnabled: oneOfType([bool, shape({})]),
  /** Enables Backwards BOGO promotions. The config service can enable or disable as well. Default: `false` */
  bogoDollarThresholdEnabled: oneOfType([bool, shape({})]),
  /**
   * A function that returns a bool that enables multi-select/multi-item add-to-cart
   * primary use case: major-appliance BMSM add-to-cart
   */
  isMultiSelect: func,
  /** Decides the type of UI to render */
  type: oneOf(['card', 'pod']),
  /** callback invoked if data is not displayable */
  onFail: func,
};

PromotionProducts.defaultProps = {
  bogoEnabled: null,
  msbEnabled: null,
  bmsmEnabled: null,
  backwardsBogoEnabled: null,
  bogoDollarThresholdEnabled: null,
  isMultiSelect: () => false,
  type: 'pod',
  onFail: null
};

// this is the data model for the anchor item.
// This includes fields needed to parse promo, verify fulfillment, and paint CTA
PromotionProducts.dataModel = extend(
  {
    product: params({ itemId: stringType().isRequired() }).shape(extend(
      {
        dataSources: stringType(),
        itemId: stringType(),
        pricing: params({ storeId: stringType().isRequired() }).shape({
          alternate: shapeType({
            bulk: shapeType({
              value: numberType({ float: true }),
            }),
          }),
          value: numberType({ float: true }),
          conditionalPromotions: arrayOfType(shapeType({
            dates: shapeType({
              start: stringType(),
              end: stringType(),
            }),
            description: shapeType({
              shortDesc: stringType(),
              longDesc: stringType(),
            }),
            experienceTag: stringType(),
            subExperienceTag: stringType(),
            eligibilityCriteria: arrayOfType(shapeType({
              itemGroup: stringType(),
              minPurchaseAmount: numberType({ float: true }),
              minPurchaseQuantity: numberType({ float: true }),
              relatedSkusCount: numberType(),
              omsSkus: arrayOfType(stringType()),
            })),
            reward: shapeType({
              tiers: arrayOfType(shapeType({
                minPurchaseAmount: numberType({ float: true }),
                minPurchaseQuantity: numberType({ float: true }),
                rewardPercent: numberType({ float: true }),
                rewardAmountPerOrder: numberType({ float: true }),
                rewardAmountPerItem: numberType({ float: true }),
                rewardFixedPrice: numberType({ float: true }),
                maxAllowedRewardAmount: numberType({ float: true }),
                maxPurchaseQuantity: numberType({ float: true }),
              })),
            }),
            nvalues: arrayOfType(stringType()),
            brandRefinementId: stringType(),
          })),
          preferredPriceFlag: boolType(),
        }),
        fulfillment: client(params({ storeId: stringType(), zipCode: stringType() }).shape({
          fulfillmentOptions: arrayOfType(shapeType({
            fulfillable: boolType(),
            type: stringType(),
            services: arrayOfType(shapeType({
              type: stringType(),
              locations: arrayOfType(shapeType({
                isAnchor: boolType(),
                locationId: stringType(),
                inventory: shapeType({
                  isOutOfStock: boolType(),
                  quantity: numberType(),
                }),
              })),
            })),
          })),
        })),
        media: shapeType({
          image: shapeType({
            url: stringType(),
          }).client(),
          images: arrayOfType(shapeType({
            url: stringType(),
            sizes: arrayOfType(stringType()),
            type: stringType(),
            subType: stringType(),
          })),
        }),
        identifiers: shapeType({
          brandName: stringType(),
          productType: stringType(),
        }),
      },
      CallToActionDataModel.product,
    )),
  },
);
